import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { TRPCClientError } from "@trpc/client";
import { useCallback, useEffect, useMemo, useState } from "react";

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
  /** Skip the initial session lookup for public shells that only need cached token state. */
  skipInitialQuery?: boolean;
};

// 简单的 JWT payload 解码（不验证签名，仅用于读取用户信息）
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // Base64url decode
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "=");
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

// 从 JWT token 中读取用户信息
function getUserFromToken(): { id: number; email: string; name: string | null; role: string } | null {
  try {
    const adminToken = localStorage.getItem("admin-jwt-token");
    const userToken = localStorage.getItem("user-jwt-token");
    const token = adminToken || userToken;
    if (!token) return null;

    const payload = decodeJwtPayload(token);
    if (!payload) return null;

    const { userId, email, role, name } = payload as Record<string, unknown>;
    if (typeof userId !== "number" || typeof email !== "string") return null;

    // 检查 token 是否过期
    if (typeof payload.exp === "number" && payload.exp * 1000 < Date.now()) {
      // Token 已过期，清除
      localStorage.removeItem("admin-jwt-token");
      localStorage.removeItem("user-jwt-token");
      localStorage.removeItem("manus-runtime-user-info");
      return null;
    }

    return {
      id: userId,
      email,
      name: typeof name === "string" ? name : null,
      role: typeof role === "string" ? role : "user",
    };
  } catch {
    return null;
  }
}

export function useAuth(options?: UseAuthOptions) {
  const { redirectOnUnauthenticated = false, redirectPath = getLoginUrl() } =
    options ?? {};
  const utils = trpc.useUtils();

  // 从 localStorage JWT token 读取用户信息（不依赖 cookie）
  const [tokenUser, setTokenUser] = useState(() => getUserFromToken());
  const shouldQuerySession = !options?.skipInitialQuery || Boolean(tokenUser) || redirectOnUnauthenticated;

  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false, // 关闭窗口焦点刷新，避免 iframe 环境下的 cookie 问题
    refetchInterval: false, // 关闭自动刷新
    // Public shells can render immediately without waiting for a session lookup.
    // Protected layouts keep the default query behavior and still resolve cookie sessions.
    enabled: shouldQuerySession,
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      utils.auth.me.setData(undefined, null);
    },
  });

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error: unknown) {
      if (
        error instanceof TRPCClientError &&
        error.data?.code === "UNAUTHORIZED"
      ) {
        return;
      }
      // 忽略其他错误，继续清除本地状态
    } finally {
      // 清除所有本地存储的认证信息
      localStorage.removeItem("admin-jwt-token");
      localStorage.removeItem("user-jwt-token");
      localStorage.removeItem("manus-runtime-user-info");
      utils.auth.me.setData(undefined, null);
      setTokenUser(null);
      await utils.auth.me.invalidate();
    }
  }, [logoutMutation, utils]);

  // 当 auth.me 返回数据时，更新 localStorage 缓存
  useEffect(() => {
    if (meQuery.data) {
      localStorage.setItem(
        "manus-runtime-user-info",
        JSON.stringify(meQuery.data)
      );
    }
  }, [meQuery.data]);

  // 监听 localStorage 变化（当 AdminLogin 写入 token 后更新状态）
  useEffect(() => {
    const handleStorageChange = () => {
      setTokenUser(getUserFromToken());
    };
    window.addEventListener("storage", handleStorageChange);
    // 也监听自定义事件（同页面内的 localStorage 变化）
    window.addEventListener("auth-token-updated", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth-token-updated", handleStorageChange);
    };
  }, []);

  const state = useMemo(() => {
    // 优先使用 auth.me 的实时数据（如果 cookie 可用）
    // 其次使用 JWT token 中的用户信息（不依赖 cookie）
    const user = meQuery.data ?? tokenUser ?? null;
    return {
      user,
      loading: meQuery.isLoading || logoutMutation.isPending,
      error: meQuery.error ?? logoutMutation.error ?? null,
      isAuthenticated: Boolean(user),
    };
  }, [
    meQuery.data,
    meQuery.error,
    meQuery.isLoading,
    logoutMutation.error,
    logoutMutation.isPending,
    tokenUser,
  ]);

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (meQuery.isLoading || logoutMutation.isPending) return;
    if (state.user) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === redirectPath) return;

    window.location.href = redirectPath;
  }, [
    redirectOnUnauthenticated,
    redirectPath,
    logoutMutation.isPending,
    meQuery.isLoading,
    state.user,
  ]);

  return {
    ...state,
    refresh: () => {
      setTokenUser(getUserFromToken());
      return meQuery.refetch();
    },
    logout,
  };
}
